const DB_NAME = 'direct-write-thumbnails';
const STORE_NAME = 'thumb-cache';
const DB_VERSION = 1;

export type CachedThumb = {
  id: string;
  size?: number;
  lastModified?: number;
  blob: Blob;
  timestamp: number;
};

let dbPromise: Promise<IDBDatabase> | null = null;

function _openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = Promise.race([
      _openDb(),
      new Promise<IDBDatabase>((_, reject) =>
        setTimeout(() => reject(new Error('IndexedDB open timeout')), 2000)
      ),
    ]).catch((err) => {
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

export async function getCachedThumb(
  id: string,
  size?: number,
  lastModified?: number
): Promise<CachedThumb | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => {
        const result = req.result as CachedThumb | undefined;
        if (!result) {
          resolve(null);
          return;
        }
        if (typeof size === 'number' && typeof result.size === 'number' && result.size !== size) {
          resolve(null);
          return;
        }
        if (
          typeof lastModified === 'number' &&
          typeof result.lastModified === 'number' &&
          result.lastModified !== lastModified
        ) {
          resolve(null);
          return;
        }
        resolve(result);
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[ThumbDB] Failed to get thumbnail', err);
    return null;
  }
}

export async function saveCachedThumb(
  id: string,
  size: number,
  lastModified: number,
  blob: Blob
): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const record: CachedThumb = {
        id,
        size,
        lastModified,
        blob,
        timestamp: Date.now(),
      };
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[ThumbDB] Failed to save thumbnail', err);
  }
}

export async function clearThumbCache(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[ThumbDB] Failed to clear thumbnail cache', err);
  }
}
